import {
  MARKIER_STUFE_LABELS,
  markierTage,
  tagKurz,
  TASK_PRIORITIES,
  ZEITFENSTER,
  ZEITFENSTER_KURZ,
  ZEITFENSTER_LABELS,
  type TaskPriority,
  type Zeitfenster,
} from '@netcon-work/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Sun, X } from 'lucide-react'
import { useState } from 'react'
import { heuteSchluessel, useDarfMarkieren, useMarkieren } from '../lib/markierung'
import type { WorkspaceEintrag } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { hintergrundSchliesst } from './ui/hintergrund'
import { Button } from './ui/primitives'
import { nebenBestandVerwerfen } from '../lib/mutationen'

/**
 * **Schnelle Aufgabe** – der abgespeckte Anlegen-Dialog aus „Mein Tag"
 * ([Z-15](../../../../docs/phasen/zusatz-15-schnelle-aufgabe.md),
 * [E-344](../../../../docs/ENTSCHEIDUNGEN.md#e-344)).
 *
 * Nils, 14.09.2026: *„Ich möchte «meinen» Tag noch schöner verplanen können. Dass ich sagen kann,
 * was heute alles anliegt – nicht immer eine eigene Aufgabe, manchmal die von vornherein
 * feststeht, manchmal auch so Reminder. Also «Schnelle Aufgaben», wo ich der Bearbeiter
 * automatisch bin, eine Fälligkeit/Zeitfenster vergebe, Prio. Und der sich dann einsortiert."*
 *
 * ## Was hier steht und was nicht
 *
 * Drei Fragen: Titel, Wann (Zeitfenster **oder** ein Datum), Prio – dazu eine Notiz, die
 * zuklappt. Der große Dialog (`AufgabeAnlegen`) hat zwölf Felder, und die fehlenden sind hier
 * alle schon beantwortet:
 *
 * | Feld | Antwort |
 * |---|---|
 * | Arbeitswelt | **deine persönliche** – sieht niemand außer dir, belegt keinen Fokus-Platz (E-238) |
 * | Bearbeiter | **du** |
 * | Pool | keiner – mit Bearbeiter gibt es keinen (E-119) |
 * | Vorhaben, Meilenstein, Mit-Owner, Wünsche, Größe | nichts davon |
 *
 * Der Knopf erscheint deshalb nur, wenn eine persönliche Welt da ist (Nils: *„geht auch nur,
 * wenn du eine hast"*). Ohne sie gäbe es keinen Ort, an dem „nur meins" wahr wäre.
 *
 * ## Warum der Bearbeiter trotzdem gesetzt wird
 *
 * In der eigenen persönlichen Welt gehört alles mir, auch ohne Zuweisung ([E-278](../../../../docs/ENTSCHEIDUNGEN.md#e-278)).
 * Gesetzt wird er trotzdem: die Karte sagt dann „bei dir", und wird die Aufgabe später in eine
 * geteilte Welt umgehängt (Z-07.17), bleibt sie meine – statt herrenlos in einem Vorrat zu
 * landen, in dem sie nie lag.
 *
 * ## Warum kein Wechsel der aktiven Welt
 *
 * `AufgabeAnlegen` schaltet die ganze Ansicht um, weil dort Pools, Vorhaben und Menschen der
 * Zielwelt gebraucht werden. Hier wird nichts davon gebraucht – die Welt geht als `workspaceId`
 * mit, der Server prüft die Mitgliedschaft, und „Mein Tag" bleibt, wo es war.
 *
 * ## Wie sie sich einsortiert
 *
 * Über die Rangfolge, nicht über eine Sonderregel: seit Z-15 stuft der Vergleicher nach dem
 * wirksamen Termin ein ([E-343](../../../../docs/ENTSCHEIDUNGEN.md#e-343)). „Fällig heute" oder
 * Zeitfenster „Jetzt" stehen damit direkt über „Jetzt", eine Prio A vor B. Nichts hier weiß,
 * dass es eine Schnelle Aufgabe ist – es ist eine Aufgabe mit weniger Fragen.
 */
export function SchnelleAufgabe({
  schliessen,
  welt,
  ichId,
  vorbelegt,
  ausfuehrlich,
}: {
  schliessen: () => void
  /** Die persönliche Arbeitswelt, in die die Aufgabe fällt. */
  welt: Pick<WorkspaceEintrag, 'id' | 'label' | 'icon'>
  ichId: string
  /** Aus „Teilen nach Work" (Z-16): Titel und Notiz stehen schon drin, der Mensch prüft. */
  vorbelegt?: { titel?: string; notiz?: string }
  /**
   * **Der Weg zum großen Dialog** – nur auf dem Telefon gesetzt
   * ([Z-16](../../../../docs/phasen/zusatz-16-pwa-mein-tag.md)). Dort ist ⚡ der einzige
   * Anlege-Knopf; wer doch Pool, Vorhaben oder Wunsch-Entwickler braucht, kommt hierüber zu
   * `AufgabeAnlegen`. Am Rechner steht „+ Aufgabe" direkt daneben, deshalb fehlt der Knopf dort.
   */
  ausfuehrlich?: () => void
}) {
  const queryClient = useQueryClient()
  const [titel, setTitel] = useState(vorbelegt?.titel ?? '')
  const [notiz, setNotiz] = useState(vorbelegt?.notiz ?? '')
  /**
   * **„Wann" ist eine Frage, nicht zwei** (Nils, 14.09.2026: *„Fällig ausblenden, wenn
   * Zeitfenster gesetzt ist. Zeitfenster + «Benutzerdefiniert» und dann nur Fälligkeit anzeigen.
   * Sonst wird das mit den Feldern zu viel."*).
   *
   * Eine Stufe **oder** ein Datum: „Jetzt" heißt heute (der Anker ist der Augenblick des
   * Anlegens, E-156), „5 Tage" heißt fünf Werktage – und wer ein bestimmtes Datum meint, wählt
   * „Benutzerdefiniert" und bekommt erst dann das Feld. Beides zugleich gab es im ersten Wurf,
   * und beides zugleich ist eine Frage doppelt gestellt: die Fälligkeit überstimmt das Fenster
   * ohnehin (E-281).
   */
  const [wann, setWann] = useState<Zeitfenster | 'datum' | ''>('')
  const [faellig, setFaellig] = useState('')
  const [prio, setPrio] = useState<TaskPriority | ''>('')
  /**
   * **Gleich für Mein Tag markieren** (Nils, 08.10.2026: *„Bei der Anlage von «Schnelle
   * Aufgaben» bitte auch die Mein Tag Auswahl einfügen."*) – der gespeicherte Tag, leer ohne.
   *
   * Eine Markierung ist kein Feld der Aufgabe, sondern eine eigene Zeile je Person (Z-20).
   * Gesetzt wird sie deshalb **nach** dem Anlegen, mit der Kennung, die der Server zurückgibt –
   * über dieselbe Mutation wie überall, damit der Vorlauf sie sofort zeigt.
   */
  const [meinTag, setMeinTag] = useState('')
  const darfMarkieren = useDarfMarkieren()
  const markieren = useMarkieren()
  const [fehler, setFehler] = useState<string | null>(null)

  const anlegen = useMutation({
    mutationFn: () =>
      trpc.tasks.anlegen.mutate({
        title: titel.trim(),
        ...(notiz.trim() ? { description: notiz.trim() } : {}),
        ...(prio ? { priority: prio } : {}),
        ...(wann === 'datum' && faellig ? { dueAt: new Date(faellig) } : {}),
        poolIds: [],
        zeitfenster: wann && wann !== 'datum' ? wann : null,
        groesse: null,
        /**
         * **Wer anlegt, nimmt ab** (16.09.2026, E-355) – hier immer ich selbst. Solange ich die
         * Aufgabe auch selbst erledige, ändert das nichts (der Abnehmer nimmt sich nicht selbst
         * ab); gebe ich sie jemandem weiter, kommt sie zur Abnahme zu mir zurück.
         */
        mitOwnerPersonId: ichId,
        wunschPersonIds: [],
        vorhabenId: null,
        meilensteinId: null,
        assigneeId: ichId,
        workspaceId: welt.id,
      }),
    onSuccess: ({ id }) => {
      if (meinTag) markieren.mutate({ taskIds: [id], am: meinTag })
      void nebenBestandVerwerfen(queryClient)
      void queryClient.invalidateQueries({ queryKey: ['pools'] })
      void queryClient.invalidateQueries({ queryKey: ['analytics'] })
      schliessen()
    },
    onError: (e: Error) => setFehler(e.message),
  })

  function absenden(e: React.FormEvent) {
    e.preventDefault()
    setFehler(null)
    if (titel.trim().length < 3) return setFehler('Der Titel braucht mindestens drei Zeichen.')
    anlegen.mutate()
  }

  /**
   * **16 px auf dem Telefon, 13 am Rechner** (Z-16). iOS Safari zoomt in jedes Feld hinein,
   * dessen Schrift kleiner als 16 px ist – und zoomt danach nicht von selbst zurück. Der
   * Dialog stünde dann halb außerhalb des Bildes. `sm:` schaltet ab 640 px auf das Maß des
   * Rechners zurück, dort gibt es den Zoom nicht.
   */
  const feld =
    'mt-1 w-full rounded-[9px] border border-line2 bg-surface px-3 py-3 text-[17px] font-normal sm:px-[11px] sm:py-2 sm:text-[13px]'
  /** Beschriftung: auf dem Telefon lesbar groß, am Rechner die kleine Zeile wie bisher. */
  const beschriftung = 'block text-[13px] font-semibold text-ink2 sm:text-[11.5px]'
  const knopf = 'tap cursor-pointer py-3 font-semibold sm:py-[5px]'

  return (
    /**
     * **Auf dem Telefon ein Vollbild, am Rechner die Karte** (Z-16, nachgeschärft 14.09.2026 nach
     * Nils' erstem Blick: *„das Notiz ist zu klein … ggf der Dialog mobil optimierter"*).
     *
     * Unter 640 px füllt das Formular den Bildschirm: Kopf mit Schließen-Kreuz, Felder in
     * Lesegröße, die Notiz offen und hoch, „Anlegen" in voller Breite über der Wischkante. Es
     * scrollt der **Mittelteil**, nicht die Seite – so bleibt der Anlegen-Knopf auch dann
     * erreichbar, wenn die Tastatur den halben Bildschirm nimmt. Kein Bottom-Sheet: dort liegt
     * die Tastatur, und ob ein `fixed`-Element mitwandert, entscheidet jeder Browser anders.
     *
     * Ab `sm:` dieselbe Karte wie seit Z-15, mit einer Änderung: die Notiz ist auch dort offen.
     */
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-[60] flex items-stretch bg-black/35 sm:items-start sm:justify-center sm:p-6 sm:pt-[14vh]"
      {...hintergrundSchliesst(schliessen, titel.trim() === '' && notiz.trim() === '')}
      onKeyDown={(e) => {
        if (e.key === 'Escape') schliessen()
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Schnelle Aufgabe"
    >
      <form
        onSubmit={absenden}
        className="flex w-full min-w-0 flex-col bg-surface sm:max-h-[min(90dvh,100%)] sm:max-w-[460px] sm:overflow-y-auto sm:rounded-card sm:border sm:border-line sm:p-[18px] sm:shadow-card"
      >
        <div className="flex items-start gap-2 px-4 pt-[max(16px,env(safe-area-inset-top))] pb-2 sm:p-0">
          <h3 className="min-w-0 flex-1 text-[19px] font-bold sm:flex sm:flex-wrap sm:items-center sm:gap-x-1.5 sm:gap-y-1 sm:text-sm">
            ⚡ Schnelle Aufgabe
            <span className="mt-0.5 block text-[12.5px] font-normal text-muted sm:mt-0 sm:inline sm:text-[11.5px]">
              <span className="hidden sm:inline">· </span>in {welt.icon} {welt.label} · sieht
              niemand außer dir · du bist Bearbeiter
            </span>
          </h3>
          {/* Auf dem Telefon gibt es keine Esc-Taste – das Kreuz ist der Weg hinaus. */}
          <button
            type="button"
            onClick={schliessen}
            aria-label="Schließen"
            className="-mr-2 -mt-1 cursor-pointer rounded-lg p-2 text-muted hover:bg-[#f3f2ef] hover:text-ink sm:hidden"
          >
            <X className="size-[22px]" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 sm:flex-none sm:overflow-visible sm:p-0">
          <label className={`mt-2 sm:mt-3 ${beschriftung}`}>
            Was liegt an?
            <input
              value={titel}
              onChange={(e) => setTitel(e.target.value)}
              required
              autoFocus
              enterKeyHint="done"
              placeholder="z. B. Kunde X zurückrufen"
              className={feld}
            />
          </label>

          <div className={`mt-4 sm:mt-3 ${beschriftung}`}>
            Wann
            {/**
             * **Die Trennlinien sind der Hintergrund** (Z-16). Vorher trug jeder Knopf ab dem
             * zweiten `border-l`; auf dem Telefon bricht die Leiste zu drei Knöpfen je Reihe um
             * (`basis-[30%]`), und dann fehlte über der zweiten Reihe die Linie. `gap-px` auf
             * `bg-line2` zeichnet sie in beide Richtungen – am Rechner sieht es exakt aus wie
             * zuvor. `tap` macht jeden Knopf auf Tastgeräten 40 px hoch (Z-10.5).
             */}
            <div
              role="group"
              aria-label="Wann"
              className="mt-1 flex flex-wrap gap-px overflow-hidden rounded-[9px] border border-line2 bg-line2 text-[14px] sm:text-[12px]"
            >
              {ZEITFENSTER.map((z) => {
                const aktiv = wann === z
                return (
                  <button
                    key={z}
                    type="button"
                    aria-pressed={aktiv}
                    title={ZEITFENSTER_LABELS[z]}
                    onClick={() => setWann(aktiv ? '' : z)}
                    className={`${knopf} grow basis-[30%] px-1.5 sm:flex-1 sm:basis-0 ${
                      aktiv
                        ? z === 'jetzt'
                          ? 'bg-pool-sofort text-white'
                          : 'bg-ink text-white'
                        : 'bg-surface text-ink2 hover:bg-[#f3f2ef]'
                    }`}
                  >
                    {ZEITFENSTER_KURZ[z]}
                  </button>
                )
              })}
              <button
                type="button"
                aria-pressed={wann === 'datum'}
                title="Ein bestimmtes Datum"
                onClick={() => setWann(wann === 'datum' ? '' : 'datum')}
                className={`${knopf} grow basis-[30%] px-1.5 sm:flex-1 sm:basis-0 ${
                  wann === 'datum' ? 'bg-ink text-white' : 'bg-surface text-ink2 hover:bg-[#f3f2ef]'
                }`}
              >
                Benutzerdefiniert
              </button>
            </div>
            {wann === 'datum' && (
              <label className="mt-2 block">
                Fällig am
                <input
                  type="date"
                  value={faellig}
                  onChange={(e) => setFaellig(e.target.value)}
                  autoFocus
                  className={feld}
                />
              </label>
            )}
          </div>

          <div className={`mt-4 sm:mt-2.5 ${beschriftung}`}>
            Priorität
            <div
              role="group"
              aria-label="Priorität"
              className="mt-1 flex gap-px overflow-hidden rounded-[9px] border border-line2 bg-line2 text-[14px] sm:text-[12px]"
            >
              {TASK_PRIORITIES.map((p) => {
                const aktiv = prio === p
                return (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={aktiv}
                    onClick={() => setPrio(aktiv ? '' : p)}
                    className={`${knopf} flex-1 px-2 ${
                      aktiv ? 'bg-ink text-white' : 'bg-surface text-ink2 hover:bg-[#f3f2ef]'
                    }`}
                  >
                    {p}
                  </button>
                )
              })}
            </div>
          </div>

          {darfMarkieren && (
            <div className={`mt-4 sm:mt-2.5 ${beschriftung}`}>
              Mein Tag
              <div
                role="group"
                aria-label="Mein Tag"
                className="mt-1 flex gap-px overflow-hidden rounded-[9px] border border-line2 bg-line2 text-[14px] sm:text-[12px]"
              >
                {markierTage(heuteSchluessel()).map(({ stufe, tag }) => {
                  const aktiv = meinTag === tag
                  return (
                    <button
                      key={stufe}
                      type="button"
                      aria-pressed={aktiv}
                      title={`Für Mein Tag markieren: ${tagKurz(tag)}`}
                      onClick={() => setMeinTag(aktiv ? '' : tag)}
                      className={`${knopf} flex-1 px-2 ${
                        aktiv ? 'bg-sonne-bg text-sonne' : 'bg-surface text-ink2 hover:bg-[#f3f2ef]'
                      }`}
                    >
                      {stufe === 'heute' && (
                        <Sun size={12} className="mr-1 inline align-[-1px]" aria-hidden />
                      )}
                      {MARKIER_STUFE_LABELS[stufe]}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/**
           * **Die Notiz ist offen, nicht hinter „+ Notiz"** (Nils, 14.09.2026: *„das Notiz ist
           * zu klein"*). Ein Reminder ist oft ein Satz mit Kontext – *wen zurückrufen, warum* –
           * und ein zugeklapptes Feld sagt „nebensächlich". Auf dem Telefon ist es hoch genug
           * für ein paar Zeilen mit dem Daumen.
           */}
          <label className={`mt-4 sm:mt-2.5 ${beschriftung}`}>
            Notiz <span className="font-normal text-muted">optional · Markdown</span>
            <textarea
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              rows={4}
              placeholder="Kontext, Rückrufnummer, worum es geht …"
              className={`${feld} min-h-[150px] sm:min-h-0`}
            />
          </label>

          {fehler && (
            <p role="alert" className="mt-2 text-[13px] text-pool-sofort sm:text-[12px]">
              {fehler}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-line px-4 pt-3 pb-[max(16px,env(safe-area-inset-bottom))] sm:mt-4 sm:justify-end sm:border-0 sm:p-0">
          {ausfuehrlich && (
            <Button
              type="button"
              variant="ghost"
              onClick={ausfuehrlich}
              className="mr-auto text-muted"
              title="Der große Dialog mit Pool, Vorhaben und Wunsch-Entwickler."
            >
              Ausführlich …
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={schliessen} className="max-sm:hidden">
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={anlegen.isPending}
            className="max-sm:flex-1 max-sm:py-3 max-sm:text-[15px]"
          >
            Anlegen
          </Button>
        </div>
      </form>
    </div>
  )
}
